import React, { useEffect, useRef, useState } from "react";
import Wrapper from "../../components/wrapper/Wrapper";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  FiSearch,
  FiPlus,
  FiX,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiBookOpen,
  FiAward,
  FiTarget,
  FiHeart,
  FiEdit,
  FiSave,
  FiDownload,
  FiFilter,
} from "react-icons/fi";
import { AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import {
  getUsersAPI,
  getUsersBlockUnblockAPI,
  getUsersDeleteAPI,
  searchUsersAPI,
  exportUsersAPI,
} from "../../API/UserAPI";
import { updateUserByAdminAPI } from "../../API/UserAPI";
import {
  getAdminUserAthletePointsAPI,
  updateAdminUserAthletePointsAPI,
} from "../../API/AthletePointsAPI";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ButtonLoader from "../../components/button/ButtonLoader";
import moment from "moment";
import SimplePagination from "../../components/Pagination/SimplePagination";
import toast from "react-hot-toast";

// Component to display user initials
const UserInitials = ({ firstName, lastName, className = "" }) => {
  const getInitials = (first, last) => {
    const firstInitial = first ? first.charAt(0).toUpperCase() : "";
    const lastInitial = last ? last.charAt(0).toUpperCase() : "";
    return firstInitial + lastInitial;
  };

  const initials = getInitials(firstName, lastName);

  return (
    <div
      className={`flex items-center justify-center rounded-full bg-[--primary] text-white font-medium ${className}`}
    >
      {initials}
    </div>
  );
};

// Component to calculate and display total score percentage
const calculateTotalPercentage = (athletePointDoc) => {
  if (!athletePointDoc?.data) return null;

  const getValue = (path, defaultValue = 0) => {
    return (
      path.split(".").reduce((obj, key) => obj?.[key], athletePointDoc.data) ||
      defaultValue
    );
  };

  let socialRaw =
    getValue("socialMedia.follower.point") +
    getValue("socialMedia.engagementRate.points") +
    getValue("socialMedia.PlatformDiversity.points") +
    getValue("socialMedia.FeedPostsThisWeek.points") +
    getValue("socialMedia.StoryPostsThisWeek.points") +
    getValue("socialMedia.WeeklyPostCompletion.points") +
    getValue("socialMedia.WeeklyGoalCompletion.points");

  let brandRaw =
    getValue("BrandPartnerships.PaidDealValue.point") +
    getValue("BrandPartnerships.FreeDeals");

  let merchRaw =
    getValue("MerchSales.UnitsSoldThisWeek.point") +
    getValue("MerchSales.TopSellerBonus.point");

  let aiRaw =
    getValue("AIToolUsage.PersonalBrandingAITool.point") +
    getValue("AIToolUsage.PartnershipsAITool.point") +
    getValue("AIToolUsage.MerchAITool.point");

  const socialMax = 52;
  const brandMax = 53;
  const merchMax = 25;
  const aiMax = 25;

  let socialScore = (socialRaw / socialMax) * 40;
  let brandScore = (brandRaw / brandMax) * 30;
  let merchScore = (merchRaw / merchMax) * 15;
  let aiScore = (aiRaw / aiMax) * 15;

  let total = socialScore + brandScore + merchScore + aiScore;
  return Number(total.toFixed(2));
};

// Component for Edit Scores button with score display
const EditScoresButton = ({ userId, userName, onOpenModal }) => {
  const { data: athletePointsData, isLoading } = useQuery({
    queryKey: ["adminUserAthletePoints", userId],
    queryFn: () => getAdminUserAthletePointsAPI(userId),
    enabled: !!userId,
    staleTime: 30000, // Cache for 30 seconds
  });

  const totalScore = athletePointsData
    ? calculateTotalPercentage(athletePointsData)
    : null;

  return (
    <button
      onClick={() => onOpenModal(userId, userName)}
      className="relative inline-flex items-center justify-center p-2 border border-transparent rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors ml-2"
      title={isLoading ? "Loading score..." : totalScore !== null ? `Edit Scores - Total: ${totalScore}%` : "Edit Scores"}
    >
      {isLoading ? (
        <ButtonLoader customClass="w-4 h-4" />
      ) : (
        <>
          <FiEdit className="w-4 h-4" />
          {totalScore !== null && (
            <span className="absolute -top-1 -right-1 bg-white text-purple-600 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center border border-purple-600">
              {Math.round(totalScore)}
            </span>
          )}
        </>
      )}
    </button>
  );
};

// User Information Modal Component
const UserModal = ({ user, isOpen, onClose }) => {
  if (!isOpen || !user) return null;

  const formatDate = (date) => {
    if (!date || date === "Invalid date") return "Not provided";
    return moment(date).format("MMM DD, YYYY");
  };

  const InfoRow = ({ icon: Icon, label, value, className = "" }) => (
    <div className={`flex items-start space-x-3 py-3 ${className}`}>
      <div className="flex-shrink-0">
        <Icon className="w-5 h-5 text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 mt-1">{value || "Not provided"}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  {user?.profilePhoto ? (
                    <img
                      src={user.profilePhoto}
                      alt="profile pic"
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <UserInitials
                      firstName={user.firstName}
                      lastName={user.lastName}
                      className="h-12 w-12 text-lg"
                    />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {user.firstName} {user.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white px-6 py-4 max-h-96 overflow-y-auto">
            <div className="space-y-1">
              <InfoRow icon={FiMail} label="Email" value={user.email} />
              <InfoRow
                icon={FiPhone}
                label="Phone Number"
                value={user.phoneNumber}
              />
              <InfoRow
                icon={FiMapPin}
                label="Home Town"
                value={user.homeTown}
              />
              <InfoRow
                icon={FiCalendar}
                label="Date of Birth"
                value={formatDate(user.dateOfBirth)}
              />
              <InfoRow
                icon={FiBookOpen}
                label="School Name"
                value={user.schoolName}
              />
              <InfoRow
                icon={FiAward}
                label="School Graduation Year"
                value={user.schoolGraduationYear}
              />
              <InfoRow icon={FiTarget} label="Sports" value={user.sports} />
              <InfoRow
                icon={FiUser}
                label="Sports Position"
                value={user.sportsPosition}
              />
              <InfoRow
                icon={FiHeart}
                label="Interests"
                value={user.interests}
              />
              <InfoRow
                icon={FiUser}
                label="Status"
                value={
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isBlocked
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {user.isBlocked ? "Blocked" : "Active"}
                  </span>
                }
                className="border-t border-gray-200 pt-4"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Users = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [userId, setUserId] = useState({ loader: false, Id: null });
  const [actionLoader, setActionLoader] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10); // Default limit is 10
  const [totalItems, settotalItems] = useState(0); // Default limit is 10
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // User editing states
  const [showUserEditModal, setShowUserEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState({});
  const [userUpdateLoading, setUserUpdateLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortBy, setSortBy] = useState(null); // null, 'name', 'createdAt', 'lastLoginAt'
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' or 'desc'
  const [isVerifiedFilter, setIsVerifiedFilter] = useState(null); // null, true, false
  const [isBlockedFilter, setIsBlockedFilter] = useState(null); // null, true, false
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [scoringUserId, setScoringUserId] = useState(null);
  const [scoringUserName, setScoringUserName] = useState("");
  const [editingPoints, setEditingPoints] = useState({});
  const [pointsUpdateLoading, setPointsUpdateLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const handlePageChange = (page) => setPageNumber(page);
  const menuRef = useRef(null);

  // Handle sort click: asc -> desc -> reset (null)
  const handleSort = (field) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        // Change to desc
        setSortOrder("desc");
      } else {
        // Reset to default (no sorting)
        setSortBy(null);
        setSortOrder("desc");
      }
    } else {
      // New field, start with asc
      setSortBy(field);
      setSortOrder("asc");
    }
    setPageNumber(1); // Reset to first page when sorting changes
  };

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPageNumber(1); // Reset to first page when searching
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Use React Query to fetch users
  // Use React Query to fetch users
  const {
    data: UsersData,
    isLoading: UserLoading,
    refetch,
  } = useQuery({
    queryKey: ["users", pageNumber, limit, debouncedSearchTerm, sortBy, sortOrder, isVerifiedFilter, isBlockedFilter], // Include all filter params in queryKey
    queryFn: () => {
      const params = {
        pageNumber,
        limit,
      };
      
      // Only add sort params if sortBy is set
      if (sortBy) {
        params.sortBy = sortBy;
        params.sortOrder = sortOrder;
      }
      
      // Add filter params
      if (isVerifiedFilter !== null) {
        params.isVerified = isVerifiedFilter;
      }
      if (isBlockedFilter !== null) {
        params.isBlocked = isBlockedFilter;
      }
      
      if (debouncedSearchTerm && debouncedSearchTerm.trim() !== "") {
        return searchUsersAPI({
          searchTerm: debouncedSearchTerm.trim(),
          ...params,
        });
      } else {
        return getUsersAPI(params);
      }
    },
    staleTime: 0,
    keepPreviousData: true,
  });

  const handleLimitChange = async (newLimit) => {
    setLimit(newLimit); // Update limit state
    setPageNumber(1); // Reset to the first page
    refetch(); // Trigger refetch with updated state
  };

  const queryClient = useQueryClient();

  const handleUserAction = async (action, userId) => {
    setActionLoader(true);
    setUserId({ loader: true, Id: userId });

    try {
      if (action === "delete") {
        await getUsersDeleteAPI(userId);
      } else if (action === "block" || action === "unblock") {
        await getUsersBlockUnblockAPI(userId);
      }

      // Refetch user data after action
      queryClient.invalidateQueries(["users"]);
    } catch (error) {
      console.error(`Error performing ${action} on user:`, error);
    } finally {
      setActionLoader(false); // Hide the loader after action is completed
      setUserId({ loader: false, Id: null });
    }
  };

  const handleToggle = (rowId) => {
    if (rowId) setUserId({ loader: false, Id: rowId });
  };

  const handleViewAnsweredQuestions = (userId, userName) => {
    // Navigate to user's answered questions page
    window.open(
      `/user-questions/${userId}?name=${encodeURIComponent(userName)}`,
      "_blank"
    );
  };

  const handleViewUserGoals = (userId, userName) => {
    // Navigate to user's goals page
    window.open(
      `/user-goals/${userId}?name=${encodeURIComponent(userName)}`,
      "_blank"
    );
  };

  const handleOpenScoringModal = (userId, userName) => {
    setScoringUserId(userId);
    setScoringUserName(userName);
    setShowScoringModal(true);
  };

  const handleCloseScoringModal = () => {
    setShowScoringModal(false);
    setScoringUserId(null);
    setScoringUserName("");
    setEditingPoints({});
    setFieldErrors({});
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  // User editing functions
  const initializeEditingUser = (userData) => {
    if (!userData) return;
    setEditingUser({
      firstName: userData.firstName || "",
      lastName: userData.lastName || "",
      email: userData.email || "",
      phoneNumber: userData.phoneNumber || "",
      dateOfBirth: userData.dateOfBirth
        ? moment(userData.dateOfBirth).format("YYYY-MM-DD")
        : "",
      homeTown: userData.homeTown || "",
      schoolName: userData.schoolName || "",
      schoolGraduationYear: userData.schoolGraduationYear || "",
      sports: userData.sports || "",
      sportsPosition: userData.sportsPosition || "",
      jersey_number: userData.jersey_number || "",
      higherEducation: userData.higherEducation || "",
      WhoHereAboutUs: userData.WhoHereAboutUs || "",
      interests: userData.interests || [],
      gender: userData.gender || "",
      isverfied: userData.isverfied || false,
      isBlocked: userData.isBlocked || false,
    });
  };

  const handleUserUpdate = async () => {
    if (Object.keys(editingUser).length === 0) {
      alert("No changes to save");
      return;
    }

    // Clean up the data before sending
    const cleanedData = { ...editingUser };

    // Convert empty string gender to null for validation
    if (cleanedData.gender === "") {
      cleanedData.gender = null;
    }

    // Convert empty strings to null for optional fields
    const optionalFields = ["higherEducation", "WhoHereAboutUs"];
    optionalFields.forEach((field) => {
      if (cleanedData[field] === "") {
        cleanedData[field] = null;
      }
    });

    setUserUpdateLoading(true);
    try {
      await updateUserByAdminAPI(
        editingUser._id || selectedUser._id,
        cleanedData
      );
      // Invalidate user data queries to refresh the data
      queryClient.invalidateQueries(["users"]);
      queryClient.invalidateQueries(["searchUsers"]);
      setShowUserEditModal(false);
      alert("User updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user: " + (error.message || "Unknown error"));
    } finally {
      setUserUpdateLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPageNumber(1);
  };

  // Handle filter changes
  const handleVerifiedFilter = (value) => {
    if (isVerifiedFilter === value) {
      // Reset if clicking the same filter
      setIsVerifiedFilter(null);
    } else {
      setIsVerifiedFilter(value);
    }
    setPageNumber(1);
  };

  const handleBlockedFilter = (value) => {
    if (isBlockedFilter === value) {
      // Reset if clicking the same filter
      setIsBlockedFilter(null);
    } else {
      setIsBlockedFilter(value);
    }
    setPageNumber(1);
  };

  const clearAllFilters = () => {
    setIsVerifiedFilter(null);
    setIsBlockedFilter(null);
    setPageNumber(1);
  };

  const hasActiveFilters = isVerifiedFilter !== null || isBlockedFilter !== null;

  // Fetch user athlete points for scoring modal
  const { data: athletePointsData, isLoading: athletePointsLoading } = useQuery({
    queryKey: ["adminUserAthletePoints", scoringUserId],
    queryFn: () => getAdminUserAthletePointsAPI(scoringUserId),
    enabled: !!scoringUserId && showScoringModal,
  });

  // Initialize editing points when data is loaded
  useEffect(() => {
    if (athletePointsData && showScoringModal) {
      initializeEditingPoints(athletePointsData);
    }
  }, [athletePointsData, showScoringModal]);

  const handleExportUsers = async () => {
    setExportLoading(true);
    try {
      const response = await exportUsersAPI();
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "text/csv" });
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = "users_export.csv";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Users exported successfully!");
    } catch (error) {
      console.error("Error exporting users:", error);
      toast.error(
        error.response?.data?.message || "Failed to export users. Please try again."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const initializeEditingPoints = (pointsData) => {
    if (!pointsData?.data) return;

    const points = pointsData.data;
    setEditingPoints({
      "socialMedia.follower.point": points.socialMedia?.follower?.point ?? "",
      "socialMedia.engagementRate.points":
        points.socialMedia?.engagementRate?.points ?? "",
      "socialMedia.PlatformDiversity.points":
        points.socialMedia?.PlatformDiversity?.points ?? "",
      "socialMedia.FeedPostsThisWeek.points":
        points.socialMedia?.FeedPostsThisWeek?.points ?? "",
      "socialMedia.StoryPostsThisWeek.points":
        points.socialMedia?.StoryPostsThisWeek?.points ?? "",
      "socialMedia.WeeklyPostCompletion.points":
        points.socialMedia?.WeeklyPostCompletion?.points ?? "",
      "socialMedia.WeeklyGoalCompletion.points":
        points.socialMedia?.WeeklyGoalCompletion?.points ?? "",
      "BrandPartnerships.PaidDealValue.point":
        points.BrandPartnerships?.PaidDealValue?.point ?? "",
      "BrandPartnerships.FreeDeals": points.BrandPartnerships?.FreeDeals ?? "",
      "MerchSales.UnitsSoldThisWeek.point":
        points.MerchSales?.UnitsSoldThisWeek?.point ?? "",
      "MerchSales.TopSellerBonus.point":
        points.MerchSales?.TopSellerBonus?.point ?? "",
      "AIToolUsage.PersonalBrandingAITool.point":
        points.AIToolUsage?.PersonalBrandingAITool?.point ?? "",
      "AIToolUsage.PartnershipsAITool.point":
        points.AIToolUsage?.PartnershipsAITool?.point ?? "",
      "AIToolUsage.MerchAITool.point":
        points.AIToolUsage?.MerchAITool?.point ?? "",
    });
  };

  const handlePointsUpdate = async () => {
    if (Object.keys(editingPoints).length === 0) {
      toast.error("No changes to save");
      return;
    }

    // Convert string values to numbers for API call
    const numericPoints = {};
    for (const [field, value] of Object.entries(editingPoints)) {
      if (value !== "" && value !== null && value !== undefined) {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          numericPoints[field] = numValue;
        }
      }
    }

    // Check if there are any valid numeric values to send
    if (Object.keys(numericPoints).length === 0) {
      toast.error("No valid numeric values to save");
      return;
    }

    setPointsUpdateLoading(true);
    try {
      await updateAdminUserAthletePointsAPI(scoringUserId, numericPoints);
      await queryClient.refetchQueries(["adminUserAthletePoints", scoringUserId]);
      const updatedData = queryClient.getQueryData([
        "adminUserAthletePoints",
        scoringUserId,
      ]);
      if (updatedData) {
        initializeEditingPoints(updatedData);
      }
      toast.success("Points updated successfully!");
    } catch (error) {
      console.error("Error updating points:", error);
      toast.error("Failed to update points: " + (error.message || "Unknown error"));
    } finally {
      setPointsUpdateLoading(false);
    }
  };

  const handlePointsChange = (field, value) => {
    setEditingPoints((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (value === "" || value === null || value === undefined) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: "Please enter a valid number",
      }));
      return;
    }

    const maxValues = {
      "socialMedia.follower.point": 12,
      "socialMedia.engagementRate.points": 8,
      "socialMedia.PlatformDiversity.points": 4,
      "socialMedia.FeedPostsThisWeek.points": 6,
      "socialMedia.StoryPostsThisWeek.points": 4,
      "socialMedia.WeeklyPostCompletion.points": 8,
      "socialMedia.WeeklyGoalCompletion.points": 10,
      "BrandPartnerships.PaidDealValue.point": 50,
      "BrandPartnerships.FreeDeals": 3,
      "MerchSales.UnitsSoldThisWeek.point": 15,
      "MerchSales.TopSellerBonus.point": 10,
      "AIToolUsage.PersonalBrandingAITool.point": 10,
      "AIToolUsage.PartnershipsAITool.point": 7.5,
      "AIToolUsage.MerchAITool.point": 7.5,
    };

    const maxValue = maxValues[field];
    if (maxValue && numValue > maxValue) {
      setFieldErrors((prev) => ({
        ...prev,
        [field]: `Maximum value allowed is ${maxValue}`,
      }));
    } else {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const renderInputField = (field, label, maxValue) => (
    <div className="flex items-center justify-between">
      <label className="text-sm font-medium text-gray-700">{label}:</label>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={editingPoints[field] ?? ""}
            onChange={(e) => handlePointsChange(field, e.target.value)}
            className={`w-16 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center ${
              fieldErrors[field]
                ? "border-red-500 bg-red-50"
                : "border-gray-300"
            }`}
            placeholder="0"
          />
          <span className="text-xs text-gray-500">/{maxValue}</span>
        </div>
        {fieldErrors[field] && (
          <span className="text-xs text-red-500">{fieldErrors[field]}</span>
        )}
      </div>
    </div>
  );

  function calculateTotalPercentage(athletePointDoc, useEditingPoints = false) {
    const getValue = (path, defaultValue = 0) => {
      if (useEditingPoints && editingPoints[path] !== undefined) {
        const value = editingPoints[path];
        if (value === "" || value === null || value === undefined) {
          return defaultValue;
        }
        return parseFloat(value) || defaultValue;
      }
      return (
        path.split(".").reduce((obj, key) => obj?.[key], athletePointDoc) ||
        defaultValue
      );
    };

    let socialRaw =
      getValue("socialMedia.follower.point") +
      getValue("socialMedia.engagementRate.points") +
      getValue("socialMedia.PlatformDiversity.points") +
      getValue("socialMedia.FeedPostsThisWeek.points") +
      getValue("socialMedia.StoryPostsThisWeek.points") +
      getValue("socialMedia.WeeklyPostCompletion.points") +
      getValue("socialMedia.WeeklyGoalCompletion.points");

    let brandRaw =
      getValue("BrandPartnerships.PaidDealValue.point") +
      getValue("BrandPartnerships.FreeDeals");

    let merchRaw =
      getValue("MerchSales.UnitsSoldThisWeek.point") +
      getValue("MerchSales.TopSellerBonus.point");

    let aiRaw =
      getValue("AIToolUsage.PersonalBrandingAITool.point") +
      getValue("AIToolUsage.PartnershipsAITool.point") +
      getValue("AIToolUsage.MerchAITool.point");

    const socialMax = 52;
    const brandMax = 53;
    const merchMax = 25;
    const aiMax = 25;

    let socialScore = (socialRaw / socialMax) * 40;
    let brandScore = (brandRaw / brandMax) * 30;
    let merchScore = (merchRaw / merchMax) * 15;
    let aiScore = (aiRaw / aiMax) * 15;

    let total = socialScore + brandScore + merchScore + aiScore;
    return Number(total.toFixed(2));
  }

  // Helper function to render sortable header
  const renderSortableHeader = (label, sortField) => {
    const isActive = sortBy === sortField;
    const isAsc = isActive && sortOrder === "asc";
    const isDesc = isActive && sortOrder === "desc";
    
    return (
      <th
        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => handleSort(sortField)}
      >
        <div className="flex items-center gap-2">
          <span>{label}</span>
          <div className="flex flex-col -space-y-0.5">
            <AiFillCaretUp
              className={`w-3 h-3 ${
                isAsc ? "text-[--primary]" : "text-gray-300"
              }`}
            />
            <AiFillCaretDown
              className={`w-3 h-3 ${
                isDesc ? "text-[--primary]" : "text-gray-300"
              }`}
            />
          </div>
        </div>
      </th>
    );
  };

  const TABLE_HEAD = [
    { label: "User", sortable: true, field: "name" },
    { label: "Email", sortable: false },
    { label: "Verified", sortable: false },
    { label: "Blocked", sortable: false },
    { label: "Joined", sortable: true, field: "createdAt" },
    { label: "Last Login", sortable: true, field: "lastLoginAt" },
    { label: "Goals", sortable: false },
    { label: "Actions", sortable: false },
  ];

  // Assuming you have the `UsersData` and it's available at the time of setting the value
  useEffect(() => {
    if (UsersData?.data?.data?.paginationInfo) {
      settotalItems(UsersData.data.data.paginationInfo.totalItems);
      setTotalPages(UsersData?.data?.data?.paginationInfo?.totalPages);
    }
  }, [UsersData]); // Re-run when `UsersData` changes

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedElement = e.target.closest("#BsThreeDotsVertical");
      if (clickedElement) {
        const value = clickedElement.getAttribute("value");
        if (userId.Id && value && userId.Id.toString() === value.toString()) {
          setUserId({ loader: false, Id: null });
        } else {
          setUserId(() => ({
            loader: false,
            Id: value,
          }));
        }
      } else if (menuRef.current && !menuRef.current.contains(e.target)) {
        setUserId({ loader: false, Id: null });
      }
    };

    // Add the event listener
    document.addEventListener("mousedown", handleOutsideClick);

    // Cleanup function to remove the event listener
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Wrapper>
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Users</h1>
            <p className="text-gray-500 text-sm">
              Manage user accounts and permissions
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportUsers}
              disabled={exportLoading}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              {exportLoading ? (
                <>
                  <ButtonLoader customClass="w-4 h-4" />
                  Exporting...
                </>
              ) : (
                <>
                  <FiDownload className="w-4 h-4" />
                  Export Users
                </>
              )}
            </button>
            <button className="bg-[--primary] hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
              <FiPlus className="w-4 h-4" />
              Add User
            </button>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Search Bar and Filters */}
        <div className="p-6 border-b border-gray-100">
          <div className="relative mb-4">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search users by name, email, phone, sports, etc..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-transparent outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <FiFilter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            {/* Verified Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Verified:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleVerifiedFilter(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    isVerifiedFilter === true
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleVerifiedFilter(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    isVerifiedFilter === false
                      ? "bg-blue-100 text-blue-700 border border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Blocked Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Blocked:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleBlockedFilter(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    isBlockedFilter === true
                      ? "bg-red-100 text-red-700 border border-red-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                  }`}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleBlockedFilter(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    isBlockedFilter === false
                      ? "bg-green-100 text-green-700 border border-green-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="ml-auto px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1"
              >
                <FiX className="w-3 h-3" />
                Clear Filters
              </button>
            )}
          </div>

          {/* Active Filters Display */}
          {(debouncedSearchTerm || hasActiveFilters) && (
            <div className="mt-3 text-sm text-gray-500">
              {debouncedSearchTerm && (
                <span>
                  Showing results for:{" "}
                  <span className="font-medium text-gray-700">
                    "{debouncedSearchTerm}"
                  </span>
                </span>
              )}
              {hasActiveFilters && (
                <span className={debouncedSearchTerm ? " | " : ""}>
                  Filters:{" "}
                  {isVerifiedFilter !== null && (
                    <span className="font-medium text-gray-700">
                      Verified: {isVerifiedFilter ? "Yes" : "No"}
                    </span>
                  )}
                  {isBlockedFilter !== null && (
                    <span className="font-medium text-gray-700">
                      {isVerifiedFilter !== null ? ", " : ""}
                      Blocked: {isBlockedFilter ? "Yes" : "No"}
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Table */}
        {UserLoading ? (
          <div className="py-12 w-full flex justify-center items-center">
            <ButtonLoader />
          </div>
        ) : UsersData?.data?.data?.users?.length === 0 ? (
          <div className="py-12 w-full flex flex-col justify-center items-center text-gray-500">
            <FiSearch className="w-12 h-12 mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {debouncedSearchTerm ? "No users found" : "No users available"}
            </h3>
            <p className="text-sm text-gray-500">
              {debouncedSearchTerm
                ? `No users match your search for "${debouncedSearchTerm}"`
                : "There are no users in the system yet"}
            </p>
            {debouncedSearchTerm && (
              <button
                onClick={handleClearSearch}
                className="mt-4 px-4 py-2 text-sm font-medium text-[--primary] hover:text-orange-600 transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {TABLE_HEAD.map((head) => {
                    if (head.sortable && head.field) {
                      return (
                        <React.Fragment key={head.label}>
                          {renderSortableHeader(head.label, head.field)}
                        </React.Fragment>
                      );
                    }
                    return (
                      <th
                        key={head.label}
                        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {head.label}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {UsersData?.data?.data?.users?.map((rowData, index) => (
                  <tr
                    key={rowData._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    {/* User */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {rowData?.profilePhoto ? (
                            <img
                              src={rowData.profilePhoto}
                              alt="profile pic"
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <UserInitials
                              firstName={rowData.firstName}
                              lastName={rowData.lastName}
                              className="h-10 w-10 text-sm"
                            />
                          )}
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => handleUserClick(rowData)}
                            className="text-sm font-medium text-gray-900 hover:text-[--primary] transition-colors cursor-pointer text-left"
                          >
                            {rowData.firstName} {rowData.lastName}
                          </button>
                          <div className="text-sm text-gray-500">
                            @{rowData.firstName?.toLowerCase()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {rowData.email}
                      </div>
                    </td>

                    {/* Verified */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rowData.isverfied
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {rowData.isverfied ? "Verified" : "Not Verified"}
                      </span>
                    </td>

                    {/* Blocked */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rowData.isBlocked
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {rowData.isBlocked ? "Blocked" : "Not Blocked"}
                      </span>
                    </td>

                    {/* Joined */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment(rowData.createdAt).format("MM/DD/YYYY")}
                    </td>

                    {/* Last Login */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment(rowData.updatedAt).format("MM/DD/YYYY")}
                    </td>

                    {/* Goals */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() =>
                          handleViewUserGoals(
                            rowData._id,
                            `${rowData.firstName} ${rowData.lastName}`
                          )
                        }
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[--primary] hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
                      >
                        <FiTarget className="w-3 h-3 mr-1" />
                        View Goals
                      </button>
                      <EditScoresButton
                        userId={rowData._id}
                        userName={`${rowData.firstName} ${rowData.lastName}`}
                        onOpenModal={handleOpenScoringModal}
                      />
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div
                        className="relative inline-block text-left"
                        ref={menuRef}
                        id="BsThreeDotsVertical"
                        value={rowData._id}
                        onClick={() => handleToggle(rowData._id)}
                      >
                        <button className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                          <BsThreeDotsVertical className="w-4 h-4 text-gray-600" />
                        </button>
                        {userId.Id === rowData._id && (
                          <div className="absolute z-40 right-0 mt-2 w-48 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                            {actionLoader && userId.Id === rowData._id ? (
                              <div className="p-4">
                                <ButtonLoader />
                              </div>
                            ) : (
                              <div className="py-1">
                                {!rowData.isBlocked && (
                                  <button
                                    onClick={() =>
                                      handleUserAction("block", rowData._id)
                                    }
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    Block User
                                  </button>
                                )}
                                {rowData.isBlocked && (
                                  <button
                                    onClick={() =>
                                      handleUserAction("unblock", rowData._id)
                                    }
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                  >
                                    Unblock User
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleViewAnsweredQuestions(
                                      rowData._id,
                                      `${rowData.firstName} ${rowData.lastName}`
                                    )
                                  }
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  View Questions
                                </button>
                                <button
                                  onClick={() => {
                                    initializeEditingUser(rowData);
                                    setSelectedUser(rowData);
                                    setShowUserEditModal(true);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                >
                                  <FiEdit className="w-4 h-4 inline mr-2" />
                                  Edit Profile
                                </button>
                                <button
                                  onClick={() =>
                                    handleUserAction("delete", rowData._id)
                                  }
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  Delete User
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-100">
          <SimplePagination
            totalPages={totalPages}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            limit={limit}
            totalItems={totalItems}
          />
        </div>
      </div>

      {/* User Information Modal */}
      <UserModal
        user={selectedUser}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      {/* User Edit Modal */}
      {showUserEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  Edit User Profile
                </h2>
                <button
                  onClick={() => {
                    setShowUserEditModal(false);
                    setEditingUser({});
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={editingUser.firstName || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          firstName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={editingUser.lastName || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          lastName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editingUser.email || ""}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, email: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={editingUser.phoneNumber || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          phoneNumber: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={editingUser.dateOfBirth || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          dateOfBirth: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hometown
                    </label>
                    <input
                      type="text"
                      value={editingUser.homeTown || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          homeTown: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      School Name
                    </label>
                    <input
                      type="text"
                      value={editingUser.schoolName || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          schoolName: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Graduation Year
                    </label>
                    <input
                      type="text"
                      value={editingUser.schoolGraduationYear || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          schoolGraduationYear: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Jersey Number
                    </label>
                    <input
                      type="text"
                      value={editingUser.jersey_number || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          jersey_number: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sport
                    </label>
                    <input
                      type="text"
                      value={editingUser.sports || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          sports: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Position
                    </label>
                    <input
                      type="text"
                      value={editingUser.sportsPosition || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          sportsPosition: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Higher Education
                    </label>
                    <select
                      value={editingUser.higherEducation || ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          higherEducation: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select...</option>
                      <option value="High School">High School</option>
                      <option value="University / College">
                        University / College
                      </option>
                      <option value="University">University</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gender
                    </label>
                    <select
                      value={editingUser.gender ?? ""}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          gender: parseInt(e.target.value) || "",
                        })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select...</option>
                      <option value="0">Male</option>
                      <option value="1">Female</option>
                      <option value="2">Non-binary</option>
                      <option value="3">Other</option>
                      <option value="4">Prefer not to say</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about us?
                  </label>
                  <select
                    value={editingUser.WhoHereAboutUs || ""}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        WhoHereAboutUs: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    <option value="Google Search">Google Search</option>
                    <option value="Friend or Referral">
                      Friend or Referral
                    </option>
                    <option value="Social Media (Instagram, Facebook, etc.)">
                      Social Media (Instagram, Facebook, etc.)
                    </option>
                    <option value="Online Advertisement">
                      Online Advertisement
                    </option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingUser.isverfied || false}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          isverfied: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Verified
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingUser.isBlocked || false}
                      onChange={(e) =>
                        setEditingUser({
                          ...editingUser,
                          isBlocked: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Blocked
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowUserEditModal(false);
                    setEditingUser({});
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUserUpdate}
                  disabled={userUpdateLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-[--primary] border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {userUpdateLoading ? (
                    <>
                      <ButtonLoader />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scoring Modal */}
      {showScoringModal && scoringUserId && athletePointsData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit Scores for {scoringUserName}
                </h2>
                <button
                  onClick={handleCloseScoringModal}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {athletePointsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <ButtonLoader />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Total Score */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Total Score:{" "}
                      {calculateTotalPercentage(athletePointsData.data, true)}%
                    </h3>
                    <p className="text-sm text-gray-600">
                      This score updates in real-time as you modify the points
                      below
                    </p>
                  </div>

                  {/* Social Media (40%) */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      📱 Social Media Activity (40% weight)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderInputField(
                        "socialMedia.follower.point",
                        "Follower Count",
                        "12"
                      )}
                      {renderInputField(
                        "socialMedia.engagementRate.points",
                        "Engagement Rate",
                        "8"
                      )}
                      {renderInputField(
                        "socialMedia.PlatformDiversity.points",
                        "Platform Diversity",
                        "4"
                      )}
                      {renderInputField(
                        "socialMedia.FeedPostsThisWeek.points",
                        "Feed Posts",
                        "6"
                      )}
                      {renderInputField(
                        "socialMedia.StoryPostsThisWeek.points",
                        "Story Posts",
                        "4"
                      )}
                      {renderInputField(
                        "socialMedia.WeeklyPostCompletion.points",
                        "Post Completion",
                        "8"
                      )}
                      {renderInputField(
                        "socialMedia.WeeklyGoalCompletion.points",
                        "Goal Completion",
                        "10"
                      )}
                    </div>
                  </div>

                  {/* Brand Partnerships (30%) */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      🤝 Brand Partnerships (30% weight)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderInputField(
                        "BrandPartnerships.PaidDealValue.point",
                        "Paid Deals",
                        "50"
                      )}
                      {renderInputField(
                        "BrandPartnerships.FreeDeals",
                        "Free Deals",
                        "3"
                      )}
                    </div>
                  </div>

                  {/* Merch Sales (15%) */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      🛍️ Merch Sales (15% weight)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderInputField(
                        "MerchSales.UnitsSoldThisWeek.point",
                        "Units Sold",
                        "15"
                      )}
                      {renderInputField(
                        "MerchSales.TopSellerBonus.point",
                        "Top Seller Bonus",
                        "10"
                      )}
                    </div>
                  </div>

                  {/* AI Tool Usage (15%) */}
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      🤖 AI Tool Usage (15% weight)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderInputField(
                        "AIToolUsage.PersonalBrandingAITool.point",
                        "Personal Branding",
                        "10"
                      )}
                      {renderInputField(
                        "AIToolUsage.PartnershipsAITool.point",
                        "Partnerships",
                        "7.5"
                      )}
                      {renderInputField(
                        "AIToolUsage.MerchAITool.point",
                        "Merch Tools",
                        "7.5"
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-3 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Changes are saved when you click "Save Changes"
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCloseScoringModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePointsUpdate}
                  disabled={
                    pointsUpdateLoading ||
                    Object.keys(editingPoints).length === 0 ||
                    athletePointsLoading
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-[--primary] border border-transparent rounded-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[--primary] transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {pointsUpdateLoading ? (
                    <>
                      <ButtonLoader />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Wrapper>
  );
};

export default Users;
